import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { KpiCard } from '../common/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { AlertTriangle, Lock, Shield, BarChart3, ArrowRight, PlusCircle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

export default function RiskManagerDashboard() {
  const navigate = useNavigate();
  const [riskSummary, setRiskSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/risk/summary'),
      api.get('/api/dashboard/stats'),
    ]).then(([risk, dash]) => {
      setRiskSummary(risk);
      setStats(dash);
    }).catch(e => toast.error(e.message || 'Failed to load risk data')).finally(() => setLoading(false));
  }, []);

  const dpdData = stats ? [
    { name: 'Current', value: stats.total_loans - stats.dpd_0_30 - stats.dpd_31_60 - stats.dpd_61_90 - stats.dpd_90_plus, color: 'hsl(174 84% 45%)' },
    { name: '1-30 DPD', value: stats.dpd_0_30, color: 'hsl(32 94% 55%)' },
    { name: '31-60 DPD', value: stats.dpd_31_60, color: 'hsl(25 95% 50%)' },
    { name: '61-90 DPD', value: stats.dpd_61_90, color: 'hsl(0 72% 60%)' },
    { name: '90+ DPD', value: stats.dpd_90_plus, color: 'hsl(0 72% 45%)' },
  ] : [];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="risk-manager-dashboard">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {['s1','s2','s3','s4'].map(id => <Skeleton key={id} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const npaRate = stats?.total_loans ? ((stats.npa_loans / stats.total_loans) * 100).toFixed(2) : '0.00';
  const npaRateNum = parseFloat(npaRate);
  let npaVariant = 'success';
  if (npaRateNum > 5) npaVariant = 'danger';
  else if (npaRateNum > 2) npaVariant = 'warning';

  return (
    <div className="space-y-6" data-testid="risk-manager-dashboard">
      <div className="gradient-header -mx-4 md:-mx-6 lg:-mx-8 -mt-6 px-4 md:px-6 lg:px-8 py-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Risk Manager Dashboard</h1>
            <p className="text-muted-foreground mt-1">Portfolio health and risk exposure</p>
          </div>
          <Button onClick={() => navigate('/risk')} data-testid="go-to-risk-btn">
            <Shield className="h-4 w-4 mr-2" />Risk & Classification
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <KpiCard title="NPA Loans" value={riskSummary?.npa_loans || 0} icon={AlertTriangle} variant={riskSummary?.npa_loans > 0 ? 'danger' : 'default'} data-testid="rm-kpi-npa" />
        <KpiCard title="NPA Rate" value={`${npaRate}%`} icon={BarChart3} variant={npaVariant} data-testid="rm-kpi-npa-rate" />
        <KpiCard title="Frozen Loans" value={riskSummary?.frozen_loans || 0} icon={Lock} variant={riskSummary?.frozen_loans > 0 ? 'warning' : 'default'} data-testid="rm-kpi-frozen" />
        <KpiCard title="Classifications" value={riskSummary?.classifications_configured || 0} icon={Shield} data-testid="rm-kpi-classifications" />
        <KpiCard title="Closed Loans" value={stats?.closed_loans || 0} icon={Lock} data-testid="rm-kpi-closed-loans" />
        <KpiCard title="New Loans" value={stats?.new_loans || 0} icon={PlusCircle} variant="success" data-testid="rm-kpi-new-loans" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">DPD Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dpdData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [value, 'Loans']} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {dpdData.map((entry) => (
                      <Cell key={`cell-${entry.name}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Risk Snapshot</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/risk')} data-testid="view-risk-details-btn">
              Details <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Total Portfolio</span>
                <span className="text-sm font-semibold tabular-nums">{stats?.total_loans || 0} loans</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">NPA Amount</span>
                <span className="text-sm font-semibold tabular-nums text-rose-600">₹{Number(stats?.npa_amount || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Total Outstanding</span>
                <span className="text-sm font-semibold tabular-nums">₹{Number(stats?.total_outstanding || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">NPA Log Entries</span>
                <span className="text-sm font-semibold tabular-nums">{riskSummary?.npa_log_entries || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-muted-foreground">DPD Log Entries</span>
                <span className="text-sm font-semibold tabular-nums">{riskSummary?.dpd_log_entries || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
